import React from 'react';
import { useApp } from '../context/AppContext';
import { formatCurrency, formatDate, discountPercent } from '../lib/format';
import { scrollToSection } from '../lib/sectionNav';
import { CountdownTimer } from './CountdownTimer';
import { Star, CalendarDays, Clock, Users, ArrowDown, ShieldCheck } from 'lucide-react';

/**
 * The header image and the one workshop this page sells.
 *
 * The whole site is a single funnel now, so there is no catalogue to choose
 * from — the visitor arrives from an ad for this workshop and the first screen
 * has to answer what it is, when it runs and what it costs, without scrolling.
 */
export const FeaturedWorkshop = ({ workshop }) => {
  const { openRegistration } = useApp();

  const off = discountPercent(workshop.originalPrice, workshop.price);
  const seats = Number(workshop.seatsLeft) || 0;

  return (
    <section id="workshop" className="relative bg-ink-950 text-white overflow-hidden">
      {/* ── Header image ──────────────────────────────────────────────────── */}
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-ink-900">
        {workshop.banner && (
          <img
            src={workshop.banner}
            alt={workshop.title}
            // The LCP element on a paid-traffic page: never lazy, never async.
            fetchPriority="high"
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}

        {/* Scrim, so the copy below reads against any photograph. */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to top, var(--color-ink-950) 4%, rgba(12,11,10,0.72) 34%, rgba(12,11,10,0.12) 72%, rgba(12,11,10,0.35) 100%)',
          }}
          aria-hidden="true"
        />

        <div className="absolute top-4 left-0 right-0 shell flex items-start justify-between gap-3">
          <span className="pill glass-dark text-gold-300">{workshop.category}</span>
          {workshop.badge && (
            <span className="pill bg-brand-600 text-white border border-white/20 shadow-brand">
              {workshop.badge}
            </span>
          )}
        </div>
      </div>

      {/* ── The offer ─────────────────────────────────────────────────────── */}
      <div className="relative shell -mt-16 pb-10">
        {workshop.rating > 0 && (
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-0.5 text-gold-400">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-3.5 h-3.5 fill-current" />
              ))}
            </span>
            <span className="text-xs font-bold text-white" data-numeric>
              {workshop.rating}
            </span>
            <span className="text-xs text-white/50">
              ({workshop.reviewsCount} reviews)
            </span>
          </div>
        )}

        {/* Sans, not Playfair. The serif is now reserved for the section
            headings further down, so the one thing being sold is set apart
            from the furniture around it rather than blending into it. */}
        <h1 className="mt-3 font-sans text-display-xl font-extrabold tracking-[-0.03em] text-balance">
          {workshop.title}
        </h1>

        {workshop.description && (
          <p className="mt-3.5 text-[0.9rem] leading-relaxed text-white/65">
            {workshop.description}
          </p>
        )}

        {/* When it runs */}
        <dl className="mt-6 grid grid-cols-3 gap-2.5">
          {[
            { icon: CalendarDays, label: 'Starts', value: formatDate(workshop.date) },
            { icon: Clock, label: 'Time', value: workshop.time },
            { icon: Users, label: 'Format', value: workshop.duration },
          ].map(({ icon: Icon, label, value }) => (
            <div key={label} className="glass-dark rounded-2xl px-3 py-3">
              <Icon className="w-4 h-4 text-gold-400" />
              <dt className="mt-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-white/40">
                {label}
              </dt>
              <dd className="text-[0.72rem] font-bold leading-snug mt-0.5">{value}</dd>
            </div>
          ))}
        </dl>

        <CountdownTimer date={workshop.date} time={workshop.time} />

        {/* Price + CTA */}
        <div className="mt-6 flex items-end justify-between gap-4">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/40">
              Your seat
            </p>
            <p className="mt-1 flex items-baseline gap-2">
              {/* Sans, like the title and the clock above it. The serif is the
                  section-heading voice further down the page; the figure the
                  visitor is being asked to pay belongs to the offer. */}
              <span
                className="font-sans text-3xl font-extrabold tracking-[-0.03em] text-gold-300"
                data-numeric
              >
                {formatCurrency(workshop.price)}
              </span>
              {off > 0 && (
                <span className="text-sm text-white/35 line-through" data-numeric>
                  {formatCurrency(workshop.originalPrice)}
                </span>
              )}
            </p>
          </div>
          {off > 0 && (
            <span className="pill bg-emerald-500/15 text-emerald-300 border border-emerald-400/25">
              {off}% off
            </span>
          )}
        </div>

        <button
          onClick={() => openRegistration(workshop)}
          className="btn btn-primary btn-lg w-full mt-5"
        >
          Reserve my seat
        </button>

        <div className="mt-3 flex items-center justify-center gap-4 text-[11px] text-white/45">
          <span className="inline-flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-pay-400" />
            Cashfree secured
          </span>
          {seats > 0 && (
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-500 animate-pulse" />
              {seats} seats left
            </span>
          )}
        </div>

        {/* Scroll cue — the page is one continuous story and the next chapter
            has to look like it exists. */}
        <button
          onClick={() => scrollToSection('#curriculum')}
          className="mt-8 mx-auto flex flex-col items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/35 hover:text-white/70 transition-colors"
        >
          See the plan & reviews
          <ArrowDown className="w-4 h-4 animate-bounce" />
        </button>
      </div>
    </section>
  );
};
